/*
 * <bc-sale-property> — keeps the PDP form's un-prefixed `Sale` line-item
 * property in step with the selected variant.
 *
 * The input itself is rendered (and given its correct initial value) by
 * snippets/bc-sale-property.liquid, so a no-JS add-to-cart already carries the
 * right label. This script exists for ONE reason: switching variants inside a
 * PDP must not leave a stale label on the form. It never creates the input and
 * never touches any other properties[...] input — the sewn-loop End 1 / End 2
 * radios are untouched by construction.
 *
 * THE RULE (mirrored in snippets/bc-campaign-label.liquid — which documents it
 * in full — and in assets/bc-segment-builder.js's _saleProperty; change all
 * three together):
 *
 *   percent = round((compare_at - price) / compare_at * 100), 0 when not on sale
 *   value   = "<percent>% off · <campaign name>", or "<percent>% off" when no
 *             campaign is matched, or "" when percent is 0.
 *
 * The percent is ALWAYS the line's own; the campaign lends only its name, which
 * is what keeps the value truthful when a campaign discounts different products
 * by different amounts. The campaign is matched off script[data-bc-campaigns]
 * (snippets/bc-campaign-data.liquid) by percent, else by being the sole
 * "varies" campaign, else by being the sole campaign running at all.
 *
 * "Not on sale" is expressed by DISABLING the input, not by blanking it. A
 * disabled input is serialized by none of the three add paths — the plain
 * `new FormData(form)` POST included — so no order line can ever carry an empty
 * "Sale:" row, without depending on Shopify's blank-value handling.
 */
(function () {
  'use strict';

  /** @returns {Array<{label: string, percent: number, varies: boolean}>} */
  function runningCampaigns() {
    // Page-global, rendered once from layout/theme.liquid. Absent === none
    // running, which is the normal state and not an error.
    const el = document.querySelector('script[data-bc-campaigns]');
    if (!el) return [];
    try {
      const parsed = JSON.parse(el.textContent || '[]');
      if (!Array.isArray(parsed)) return [];
      // A nameless campaign has nothing to lend, and counting it would only
      // ever stop some other campaign from looking unique below.
      return parsed.filter((c) => c && typeof c.label === 'string' && c.label !== '');
    } catch (_e) {
      return [];
    }
  }

  function percentOff(priceCents, compareCents) {
    if (!Number.isFinite(priceCents) || !Number.isFinite(compareCents)) return 0;
    if (compareCents <= priceCents || compareCents <= 0) return 0;
    return Math.round(((compareCents - priceCents) / compareCents) * 100);
  }

  /**
   * The campaign NAME for a percent, or '' when none can be named without
   * guessing. See snippets/bc-campaign-label.liquid for the rule in full.
   */
  function campaignName(percent) {
    const running = runningCampaigns();

    const exact = running.find((c) => c.varies !== true && Number(c.percent) === percent);
    if (exact) return exact.label;

    // Per-product discount levels publish no percent to join on, so a lone
    // "varies" campaign is identified by elimination — and a lone campaign of
    // any kind is the only sale on the store whatever its mirrored percent says.
    const varies = running.filter((c) => c.varies === true);
    if (varies.length === 1) return varies[0].label;
    if (running.length === 1) return running[0].label;

    // Ambiguous: a WRONG sale name in a confirmation email is worse than none.
    return '';
  }

  function labelFor(percent) {
    if (!(percent > 0)) return '';
    const name = campaignName(percent);
    return name ? percent + '% off · ' + name : percent + '% off';
  }

  class BcSaleProperty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-bc-sale-input]');
      if (!this.input) return;

      this.repointForm();

      this.prices = {};
      try {
        const json = this.querySelector('script[data-bc-sale-variants]');
        if (json) this.prices = JSON.parse(json.textContent || '{}');
      } catch (_e) {
        // Empty map -> #sync leaves the server-rendered value alone, which is
        // correct for the variant the page was rendered for.
      }

      this.onVariantUpdate = (event) => this.sync(event);
      // The legacy v3 event name: assets/bc-events-compat.js bridges Horizon
      // v4's shopify:product:select onto it, exactly as bc-price-total.js does.
      document.addEventListener('variant:update', this.onVariantUpdate);

      // The server-rendered value is already right for the variant this markup
      // was rendered for; this only matters when the element is re-connected
      // over content whose form already names a different variant (a morph, a
      // restored quick-add dialog). It bails harmlessly when it can't tell.
      this.sync();
    }

    disconnectedCallback() {
      if (this.onVariantUpdate) document.removeEventListener('variant:update', this.onVariantUpdate);
    }

    /**
     * The form this input belongs to. Normally the PDP's buy-buttons form,
     * whose id Liquid already wrote into the `form` attribute.
     *
     * The exception is the quick-add modal: it morphs the product page's
     * data-product-grid-content into a dialog that has its OWN
     * QuickAdd-ProductForm-… form, leaving the server-rendered association
     * pointing at a form id that does not exist on that page. Re-pointing here
     * is what keeps a sale attributed when a shopper adds from a collection
     * card. (The sewn-loop end radios have the same gap and are left alone —
     * fixing them is a separate change with its own testing.)
     */
    resolveForm() {
      const declared = document.getElementById(this.dataset.formId || '');
      if (declared) return declared;

      const scope = this.closest('quick-add-dialog') || this.closest('.shopify-section') || document;
      return scope.querySelector('form[data-type="add-to-cart-form"]');
    }

    repointForm() {
      const form = this.resolveForm();
      if (form && form.id && form.id !== this.input.getAttribute('form')) {
        this.input.setAttribute('form', form.id);
      }
    }

    /**
     * The variant the form will actually post. The form's own `id` input is the
     * authority (the variant picker and bc-variant-optimistic both write it);
     * the event's resource is the fallback for the optimistic window before a
     * section morph lands.
     */
    currentVariantId(event) {
      const form = this.resolveForm();
      const field = form && form.querySelector('input[name="id"]');
      if (field && field.value) return String(field.value);

      const resource = event && event.detail && event.detail.resource;
      return resource && resource.id ? String(resource.id) : '';
    }

    sync(event) {
      this.repointForm();
      const id = this.currentVariantId(event);
      if (!id) return;

      const pair = this.prices[id];
      // A variant this map has never heard of (a combined-listing switch to
      // another product, say): leave whatever is there rather than guessing.
      if (!Array.isArray(pair)) return;

      const value = labelFor(percentOff(Number(pair[0]), Number(pair[1])));
      this.input.value = value;
      // See the header: disabled, not empty, is what keeps a non-sale line from
      // carrying the property at all.
      this.input.disabled = value === '';
    }
  }

  if (!customElements.get('bc-sale-property')) {
    customElements.define('bc-sale-property', BcSaleProperty);
  }
})();
